import { IFilterProto } from '@monorepo/controlled/src/models/filter.model';
import { EnumFilterPrototype } from './enum.filter';
import { NumberFilterPrototype } from './number.filter';
import { StatusFilterPrototype } from './status.filter';
import { StringFilterPrototype } from './string.filter';
import { accountFilters } from '../../hooks/filters/accounts.filters';
import { advertiserFilters } from '../../hooks/filters/advertisers.filters';
import { campaignFilters } from '../../hooks/filters/campaigns.filters';
import { channelFilters } from '../../hooks/filters/channels.filters';
import { sourcesFilters } from '../../hooks/filters/sources.filters';

export interface INumberFilterOptions {
	columnName: string;
	cellKey: string;
	isPercentage?: boolean;
	entity?: string;
}

export interface IStringFilterOptions {
	columnName: string;
	cellKey: string;
	entity?: string;
}

export interface IEnumFilterOptions {
	columnName: string;
	cellKey: string;
	enumOptions: string[];
	deletable?: boolean;
	entity?: string;
}

export type FilterType = INumberFilter | IStringFilter | IEnumFilter;

export type FilterTypeKey = 'numberFilters' | 'stringFilters' | 'enumFilters';

export type IEntityFilters = {
	[key in FilterTypeKey]: FilterType;
};

export interface INumberFilter {
	[key: string]: INumberFilterOptions;
}

export interface IStringFilter {
	[key: string]: IStringFilterOptions;
}

export interface IEnumFilter {
	[key: string]: IEnumFilterOptions;
}

enum EntitiesPrefixes {
	Campaigns = 'campaigns',
	Advertisers = 'advertisers',
	Accounts = 'accounts',
	Channels = 'channels',
	Sources = 'sources',
}

const addEntitiesPrefix = (entitiesFilters: Map<FilterTypeKey, FilterType>[], entitiesPrefix: EntitiesPrefixes[]) => {
	entitiesFilters.forEach((entityFilters, index) => {
		entityFilters.forEach(filterType => {
			Object.keys(filterType).forEach((filterKey: string) => {
				filterType[filterKey] = { ...filterType[filterKey] }; // deep clone of performence filters
				filterType[filterKey].entity = entitiesPrefix[index];
			});
		});
	});
};

const setFilterPrototypes = (entityFilters: Map<FilterTypeKey, FilterType>): IFilterProto[] => {
	const filterPrototypes: IFilterProto[] = [];

	for (const key of entityFilters.keys()) {
		if (key === 'numberFilters') {
			const numberFilters = entityFilters.get(key) as INumberFilter;
			Object.keys(numberFilters).forEach(columnKey => {
				filterPrototypes.push(NumberFilterPrototype(numberFilters[columnKey]));
			});
		} else if (key === 'stringFilters') {
			const stringFilters = entityFilters.get(key) as IStringFilter;
			Object.keys(stringFilters).forEach(columnKey => {
				filterPrototypes.push(StringFilterPrototype(stringFilters[columnKey]));
			});
		} else {
			const enumFilters = entityFilters.get(key) as IEnumFilter;
			Object.keys(enumFilters).forEach(columnKey => {
				filterPrototypes.push(EnumFilterPrototype(enumFilters[columnKey]));
			});
		}
	}

	return filterPrototypes;
};

const initializePrototypeFilter = (filterPrototypes: IFilterProto[]) => {
	return filterPrototypes.reduce((prev, currentFilter) => {
		return { ...prev, [currentFilter.prop]: currentFilter };
	}, {});
};

// make sure that filers array and prefixes has the same order
addEntitiesPrefix(
	[campaignFilters, advertiserFilters, accountFilters, channelFilters, sourcesFilters],
	[
		EntitiesPrefixes.Campaigns,
		EntitiesPrefixes.Advertisers,
		EntitiesPrefixes.Accounts,
		EntitiesPrefixes.Channels,
		EntitiesPrefixes.Sources,
	]
);

export const campaignFilterPrototypes = setFilterPrototypes(campaignFilters);
export const advertiserFilterPrototypes = setFilterPrototypes(advertiserFilters);
export const accountFilterPrototypes = setFilterPrototypes(accountFilters);
export const channelFilterPrototypes = setFilterPrototypes(channelFilters);
export const sourcesFilterPrototypes = setFilterPrototypes(sourcesFilters);

export const filterPrototypesByProp: Record<string, IFilterProto> = {
	status: StatusFilterPrototype,
	...initializePrototypeFilter(campaignFilterPrototypes),
	...initializePrototypeFilter(advertiserFilterPrototypes),
	...initializePrototypeFilter(accountFilterPrototypes),
	...initializePrototypeFilter(channelFilterPrototypes),
	...initializePrototypeFilter(sourcesFilterPrototypes),
};
